import React, { useEffect, useState, ReactNode, useCallback } from 'react'
import { log, warn } from '../utils/logger'
import { notifications } from '@mantine/notifications'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { authApi } from '../services/api'
import { getIsAdminFromToken } from '../utils/jwt'
import { AuthContext } from '../hooks/useAuth'
import type { User, AuthTokens, AuthContextType, UserCredential } from '../types'
import { startRegistration, startAuthentication } from '@simplewebauthn/browser'

interface ApiError {
    response?: {
        status?: number
        data?: {
            message?: string
            error?: string
        }
    }
    message?: string
}

interface AuthProviderProps {
    children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [user, setUser] = useState<User | null>(null)
    const [tokens, setTokens] = useState<AuthTokens | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isAdminFromToken, setIsAdminFromToken] = useState<boolean>(false)
    const [hasPasskeys, setHasPasskeys] = useState<boolean>(false)

    const isAuthenticated = !!user && !!tokens
    // Use isAdmin from token immediately, fallback to user.isAdmin once loaded
    const isAdmin = user?.isAdmin ?? isAdminFromToken

    // Helper function to clear auth state
    const clearAuthState = useCallback(() => {
        setUser(null)
        setTokens(null)
        setIsAdminFromToken(false)
        localStorage.removeItem('tokens')

        // Clear all cached data when user logs out
        queryClient.clear()
        log('🗑️ Cleared all cached data on logout')
    }, [queryClient])

    // Helper function to handle post-authentication navigation
    const handleAuthenticationSuccess = () => {
        const redirectPath = localStorage.getItem('redirectAfterAuth')
        log('🎯 Handling authentication success, redirect path:', redirectPath)
        if (redirectPath) {
            log('✅ Found redirect path, navigating to:', redirectPath)
            localStorage.removeItem('redirectAfterAuth')
            // Use setTimeout to ensure navigation happens after all state updates
            setTimeout(() => {
                log('🚀 Executing delayed navigation to:', redirectPath)
                navigate(redirectPath, { replace: true })
            }, 100)
        } else {
            log('🏠 No redirect path found, navigating to home')
            setTimeout(() => {
                navigate('/', { replace: true })
            }, 100)
        }
    }

    // Listen for logout events from the API interceptor
    useEffect(() => {
        const handleLogoutEvent = () => {
            log('📢 Received auth:logout event, clearing auth state')
            clearAuthState()
            notifications.show({
                title: 'Session Expired',
                message: 'Your session has expired. Please log in again.',
                color: 'orange',
            })
        }

        log('👂 Setting up auth:logout event listener')
        window.addEventListener('auth:logout', handleLogoutEvent)
        return () => {
            log('🧹 Cleaning up auth:logout event listener')
            window.removeEventListener('auth:logout', handleLogoutEvent)
        }
    }, [clearAuthState])

    useEffect(() => {
        const initAuth = async () => {
            log('🔄 Auth initialization started')
            try {
                const storedTokens = localStorage.getItem('tokens')
                log('📦 Stored tokens in localStorage:', storedTokens ? 'Found' : 'Not found')

                if (storedTokens) {
                    let parsedTokens: AuthTokens
                    try {
                        parsedTokens = JSON.parse(storedTokens) as AuthTokens
                        log('✅ Tokens parsed successfully:', {
                            hasAccessToken: !!parsedTokens.accessToken,
                            hasRefreshToken: !!parsedTokens.refreshToken,
                            accessTokenLength: parsedTokens.accessToken?.length,
                            refreshTokenLength: parsedTokens.refreshToken?.length
                        })
                    } catch (parseError) {
                        console.error('❌ Error parsing stored tokens:', parseError)
                        localStorage.removeItem('tokens')
                        return
                    }

                    // Validate that we have both tokens
                    if (!parsedTokens.accessToken || !parsedTokens.refreshToken) {
                        warn('⚠️ Invalid token structure in localStorage')
                        localStorage.removeItem('tokens')
                        return
                    }

                    log('🔑 Setting tokens in state')
                    setTokens(parsedTokens)

                    // Immediately extract isAdmin from token
                    const isAdminFromJwt = getIsAdminFromToken(parsedTokens.accessToken)
                    log('👑 Admin status from token:', isAdminFromJwt)
                    setIsAdminFromToken(isAdminFromJwt)

                    try {
                        log('👤 Attempting to get user profile')
                        // Verify token and get user info
                        const response = await authApi.getProfile()
                        log('✅ Profile fetched successfully:', response.data.data)
                        setUser(response.data.data.user)
                    } catch (profileError: unknown) {
                        const error = profileError as ApiError
                        console.error('❌ Failed to get user profile:', {
                            status: error.response?.status,
                            message: error.response?.data?.message,
                            error: error.message
                        })
                        // If getting profile fails, the token might be invalid
                        // The API interceptor will handle token refresh automatically
                        // If that also fails, the auth:logout event will be triggered
                        if (error.response?.status === 401) {
                            log('🔄 Got 401, letting interceptor handle token refresh')
                            // Let the interceptor handle the refresh
                            // If it fails, it will trigger the logout event
                        } else {
                            log('💥 Non-401 error, clearing auth state')
                            // For other errors, clear the auth state
                            clearAuthState()
                        }
                    }
                } else {
                    log('📭 No tokens found in localStorage')
                }
            } catch (error) {
                console.error('💥 Auth initialization error:', error)
                clearAuthState()
            } finally {
                log('🏁 Auth initialization finished, setting isLoading to false')
                setIsLoading(false)
            }
        }

        initAuth()
    }, [clearAuthState])

    const login = async (email: string, password: string) => {
        try {
            log('🔐 Starting login process')
            setIsLoading(true)
            const response = await authApi.login({ email, password })
            log('📨 Login API response:', response.data)

            // Backend returns { user, token } but frontend expects { user, tokens: { accessToken, refreshToken } }
            const { user: userData, token } = response.data.data
            log('👤 Extracted user data:', userData)
            log('🔑 Extracted token:', token)

            // Create tokens object to match frontend expectations
            const tokenData = {
                accessToken: token,
                refreshToken: token // Use same token for both since backend doesn't have refresh tokens
            }
            log('🔧 Transformed token data:', tokenData)

            setUser(userData)
            setTokens(tokenData)

            // Extract isAdmin from token immediately
            const isAdminFromJwt = getIsAdminFromToken(tokenData.accessToken)
            setIsAdminFromToken(isAdminFromJwt)

            log('💾 Storing tokens in localStorage:', tokenData)
            localStorage.setItem('tokens', JSON.stringify(tokenData))

            const storedCheck = localStorage.getItem('tokens')
            log('✅ Verification - tokens stored in localStorage:', storedCheck ? 'Success' : 'Failed')

            notifications.show({
                title: 'Welcome back!',
                message: `Hello ${userData.email}`, // Use email since backend doesn't have username
                color: 'green',
            })

            // Handle post-authentication navigation
            handleAuthenticationSuccess()
        } catch (error: unknown) {
            const err = error as ApiError
            console.error('❌ Login error:', err)
            notifications.show({
                title: 'Login failed',
                message: err.response?.data?.error || 'Invalid credentials',
                color: 'red',
            })
            throw error
        } finally {
            setIsLoading(false)
        }
    }

    const register = async (email: string, password: string, country: string = 'NL') => {
        setIsLoading(true)
        try {
            const response = await authApi.register({ email, password, country })

            // Backend now returns { user } without token since email verification is required
            const { user: userData } = response.data.data

            // Store flag to show verification message on login page
            localStorage.setItem('showVerificationMessage', 'true')
            localStorage.setItem('verificationEmail', userData.email)

            // Navigate to login page immediately
            navigate('/login')
        } finally {
            setIsLoading(false)
        }
    }

    const logout = () => {
        clearAuthState()
        // Clear any stored redirect path on logout
        localStorage.removeItem('redirectAfterAuth')
        notifications.show({
            title: 'Logged out',
            message: 'You have been logged out successfully',
            color: 'blue',
        })
    }

    // Helper function to set user and tokens directly (for email verification)
    const setUserAndTokens = (userData: User, tokenData: AuthTokens) => {
        setUser(userData)
        setTokens(tokenData)

        // Extract isAdmin from token immediately
        const isAdminFromJwt = getIsAdminFromToken(tokenData.accessToken)
        setIsAdminFromToken(isAdminFromJwt)

        localStorage.setItem('tokens', JSON.stringify(tokenData))
    }

    const loginWithPasskey = async (email?: string) => {
        try {
            setIsLoading(true)
            log('🔐 Starting passkey authentication')

            // Get authentication options from server
            const optionsResponse = await authApi.passkeyAuthenticationBegin({ email })
            const options = optionsResponse.data.data

            log('📋 Got authentication options:', options)

            // Transform options to match WebAuthn library expectations
            const webAuthnOptions = {
                rpId: options.rpId,
                challenge: options.challenge,
                timeout: options.timeout,
                userVerification: options.userVerification as UserVerificationRequirement,
                allowCredentials: options.allowCredentials?.map(cred => ({
                    id: cred.id,
                    type: 'public-key' as const,
                    transports: cred.transports as AuthenticatorTransport[]
                }))
            }

            // Start WebAuthn authentication
            const authResponse = await startAuthentication({ optionsJSON: webAuthnOptions })

            log('🔑 Got authentication response:', authResponse)

            // Verify authentication with server
            const verificationResponse = await authApi.passkeyAuthenticationFinish(
                authResponse as any, // Type assertion to handle clientExtensionResults mismatch
                options.challenge
            )

            const { user: userData, token } = verificationResponse.data.data

            // Create tokens object to match frontend expectations
            const tokenData = {
                accessToken: token,
                refreshToken: token
            }

            setUser(userData)
            setTokens(tokenData)

            // Extract isAdmin from token immediately
            const isAdminFromJwt = getIsAdminFromToken(tokenData.accessToken)
            setIsAdminFromToken(isAdminFromJwt)

            localStorage.setItem('tokens', JSON.stringify(tokenData))

            notifications.show({
                title: 'Welcome back!',
                message: `Signed in with passkey`,
                color: 'green',
            })

            // Handle post-authentication navigation
            handleAuthenticationSuccess()
        } catch (error: unknown) {
            const err = error as ApiError
            console.error('❌ Passkey login error:', err)
            notifications.show({
                title: 'Passkey login failed',
                message: err.response?.data?.error || 'Authentication failed',
                color: 'red',
            })
            throw error
        } finally {
            setIsLoading(false)
        }
    }

    const registerPasskey = async (nickname?: string) => {
        try {
            if (!isAuthenticated) {
                throw new Error('Must be logged in to register a passkey')
            }

            log('🔑 Starting passkey registration')

            // Get registration options from server
            const optionsResponse = await authApi.passkeyRegistrationBegin()
            const options = optionsResponse.data.data

            log('📋 Got registration options:', options)

            // Transform options to match WebAuthn library expectations
            const webAuthnOptions = {
                rp: {
                    name: options.rp.name,
                    id: options.rp.id
                },
                user: {
                    id: options.user.id,
                    name: options.user.name,
                    displayName: options.user.displayName
                },
                challenge: options.challenge,
                pubKeyCredParams: options.pubKeyCredParams.map(param => ({
                    type: 'public-key' as const,
                    alg: param.alg
                })),
                timeout: options.timeout,
                attestation: options.attestation as AttestationConveyancePreference,
                authenticatorSelection: {
                    authenticatorAttachment: options.authenticatorSelection.authenticatorAttachment as AuthenticatorAttachment,
                    userVerification: options.authenticatorSelection.userVerification as UserVerificationRequirement,
                    residentKey: options.authenticatorSelection.residentKey as ResidentKeyRequirement
                },
                excludeCredentials: options.excludeCredentials.map(cred => ({
                    id: cred.id,
                    type: 'public-key' as const,
                    transports: cred.transports as AuthenticatorTransport[]
                })),
                supportedAlgorithmIDs: options.supportedAlgorithmIDs
            }

            const regResponse = await startRegistration({ optionsJSON: webAuthnOptions })

            log('✅ Got registration response:', regResponse)

            // Verify registration with server
            await authApi.passkeyRegistrationFinish(regResponse as any, nickname) // Type assertion to handle clientExtensionResults mismatch

            // Update hasPasskeys state
            setHasPasskeys(true)

            notifications.show({
                title: 'Passkey registered!',
                message: nickname ? `"${nickname}" has been added to your account` : 'New passkey has been added to your account',
                color: 'green',
            })
        } catch (error: unknown) {
            const err = error as ApiError
            console.error('❌ Passkey registration error:', err)
            notifications.show({
                title: 'Passkey registration failed',
                message: err.response?.data?.error || 'Registration failed',
                color: 'red',
            })
            throw error
        }
    }

    const getPasskeys = async (): Promise<UserCredential[]> => {
        try {
            if (!isAuthenticated) {
                return []
            }

            const response = await authApi.getPasskeyCredentials()
            return response.data.data
        } catch (error) {
            console.error('❌ Error fetching passkeys:', error)
            return []
        }
    }

    const deletePasskey = async (credentialId: string) => {
        try {
            await authApi.deletePasskeyCredential(credentialId)

            // Check if user still has passkeys
            const remainingPasskeys = await getPasskeys()
            setHasPasskeys(remainingPasskeys.length > 0)

            notifications.show({
                title: 'Passkey removed',
                message: 'The passkey has been removed from your account',
                color: 'green',
            })
        } catch (error: unknown) {
            const err = error as ApiError
            console.error('❌ Error deleting passkey:', err)
            notifications.show({
                title: 'Failed to remove passkey',
                message: err.response?.data?.error || 'Could not remove passkey',
                color: 'red',
            })
            throw error
        }
    }

    const registerWithPasskey = async (email: string, country: string = 'NL', nickname?: string) => {
        try {
            setIsLoading(true)
            log('🔑 Starting passwordless registration with passkey')

            // Step 1: Begin registration process
            const optionsResponse = await authApi.registerWithPasskeyBegin({ email, country })
            const options = optionsResponse.data.data

            log('📋 Got registration options:', options)

            // Extract tempUserId from options
            const { tempUserId, ...registrationOptions } = options

            // Transform registration options to match WebAuthn library expectations
            const webAuthnRegistrationOptions = {
                rp: {
                    name: registrationOptions.rp.name,
                    id: registrationOptions.rp.id
                },
                user: {
                    id: registrationOptions.user.id,
                    name: registrationOptions.user.name,
                    displayName: registrationOptions.user.displayName
                },
                challenge: registrationOptions.challenge,
                pubKeyCredParams: registrationOptions.pubKeyCredParams.map(param => ({
                    type: 'public-key' as const,
                    alg: param.alg
                })),
                timeout: registrationOptions.timeout,
                attestation: registrationOptions.attestation as AttestationConveyancePreference,
                authenticatorSelection: {
                    authenticatorAttachment: registrationOptions.authenticatorSelection.authenticatorAttachment as AuthenticatorAttachment,
                    userVerification: registrationOptions.authenticatorSelection.userVerification as UserVerificationRequirement,
                    residentKey: registrationOptions.authenticatorSelection.residentKey as ResidentKeyRequirement
                },
                excludeCredentials: registrationOptions.excludeCredentials.map(cred => ({
                    id: cred.id,
                    type: 'public-key' as const,
                    transports: cred.transports as AuthenticatorTransport[]
                })),
                supportedAlgorithmIDs: registrationOptions.supportedAlgorithmIDs
            }

            // Step 2: Start WebAuthn registration
            const registrationResponse = await startRegistration({ optionsJSON: webAuthnRegistrationOptions })

            log('✅ Got registration response:', registrationResponse)

            // Step 3: Finish registration
            const finishResponse = await authApi.registerWithPasskeyFinish({
                tempUserId,
                response: registrationResponse as any, // Type assertion to handle clientExtensionResults mismatch
                nickname
            })

            const { user: userData } = finishResponse.data.data

            // Store flag to show verification message on login page
            localStorage.setItem('showVerificationMessage', 'true')
            localStorage.setItem('verificationEmail', userData.email)

            notifications.show({
                title: 'Account created! Please verify your email',
                message: 'Check your inbox for a verification link to activate your account.',
                color: 'green',
            })

            // Navigate to login page immediately
            navigate('/login')
        } catch (error: unknown) {
            const err = error as ApiError
            console.error('❌ Passwordless registration error:', err)
            // Don't show notification - let the form handle the error display
            throw error
        } finally {
            setIsLoading(false)
        }
    }

    // No refresh token functionality needed for single token system

    const value: AuthContextType = {
        user,
        tokens,
        isLoading,
        isAuthenticated,
        isAdmin,
        login,
        register,
        registerWithPasskey,
        logout,
        loginWithPasskey,
        registerPasskey,
        getPasskeys,
        deletePasskey,
        hasPasskeys,
        setUserAndTokens,
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
} 